import { JSX, ReactNode } from 'react';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@repo/ui/components/ui/table';
import { OpenCallModel } from '../../../models';

export type OpenCallTableCell = {
  props?: {
    className: string;
  };
  children: JSX.Element;
};
export type OpenCallBodyCell = Omit<OpenCallTableCell, 'children'> & {
  children: JSX.Element | ((context: OpenCallModel) => JSX.Element);
};
export type GetRowCTAResult = {
  Wrapper: ({ children }: { children: ReactNode }) => ReactNode;
  isClickable: boolean;
};
type OpenCallsTableProps = {
  loading: boolean;
  openCalls: OpenCallModel[];
  tableProps?: {
    className?: string;
  };
  headerCells: OpenCallTableCell[];
  bodyCells: OpenCallBodyCell[];
  getRowCTA: (openCall: OpenCallModel) => GetRowCTAResult;
  totalCount?: number;
  page?: number;
  limit?: number;
};

export const OpenCallsTable = ({
  loading,
  openCalls,
  tableProps,
  headerCells,
  bodyCells,
  getRowCTA,
  totalCount,
  page = 1,
  limit = openCalls.length || 1,
}: OpenCallsTableProps) => {
  const firstVisibleItem =
    totalCount && openCalls.length ? (page - 1) * limit + 1 : 0;
  const lastVisibleItem = Math.min(
    page * limit,
    totalCount ?? openCalls.length,
  );

  return (
    <Table {...tableProps}>
      <TableHeader>
        <TableRow variant="header">
          {headerCells?.map((cell, index) => (
            <TableHead key={index} {...cell?.props}>
              {cell.children}
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {loading && (
          <TableRow className="h-14 sm:h-24 text-center">
            <TableCell colSpan={headerCells.length}>
              <span className="text-cn-muted-foreground">Loading...</span>
            </TableCell>
          </TableRow>
        )}
        {!loading &&
          openCalls?.map((openCall) => {
            const { Wrapper, isClickable } = getRowCTA(openCall);

            return (
              <TableRow
                role="table-row"
                key={openCall.id}
                variant={isClickable ? 'default' : 'nonClickable'}
                className="h-14 sm:h-24"
              >
                {bodyCells?.map((cell, index) => {
                  const children =
                    typeof cell?.children === 'function'
                      ? cell.children(openCall)
                      : cell.children;

                  return (
                    <TableCell key={index} {...cell.props} className="p-0">
                      <Wrapper>{children}</Wrapper>
                    </TableCell>
                  );
                })}
              </TableRow>
            );
          })}
      </TableBody>
      {!loading && (
        <TableCaption className="border-t text-left pt-5 pb-1 text-xs pl-4 md:pl-0 mt-0 md:mt-auto md:ml-0 text-cn-muted-foreground font-bold">
          {totalCount === undefined
            ? `Showing ${openCalls?.length} open calls`
            : totalCount === 0
              ? 'Showing 0 open calls'
              : `Showing ${firstVisibleItem}-${lastVisibleItem} of ${totalCount} open calls`}
        </TableCaption>
      )}
    </Table>
  );
};
